/**
 * Phase 45 Plan 45-08 (D-15) — acceptance criteria hook.
 * External acceptance v1 (260702) — audience-aware: an `external` audience uses
 * the raw-fetch external transport (externalBoard.ts, cookie creds +
 * X-Audience: external) hitting /tasks/:id/external-acceptance; `internal`
 * keeps the existing internal client (taskAcceptance.ts) unchanged.
 *
 * Composite hook returning the list query + four mutations against a single
 * stable, audience-scoped query key ['tasks', taskId, 'acceptance', audience].
 * Each mutation uses an optimistic update followed by rollback on error and
 * invalidation on settle (mirrors the Plan 45-07 useClaimTask pattern for
 * snapshot + statusOf branching).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AcceptanceItem,
  fetchAcceptance,
  addAcceptanceItem,
  toggleAcceptanceItem,
  updateAcceptanceItemText,
  deleteAcceptanceItem,
} from '../services/api/taskAcceptance';
import {
  fetchAcceptanceExternal,
  addAcceptanceItemExternal,
  patchAcceptanceItemExternal,
  deleteAcceptanceItemExternal,
} from '../services/api/externalBoard';
import { ApiClientError } from '../services/api/client';
import { ExternalApiError } from '../services/api/externalAuth';
import { useToast } from '../components/ui/Toast';
import { MY_TASKS_BASE_KEY } from './useMyTasks';

export type AcceptanceAudience = 'internal' | 'external';

export const acceptanceQueryKey = (
  taskId: number,
  audience: AcceptanceAudience = 'internal',
) => ['tasks', taskId, 'acceptance', audience] as const;

/**
 * Audience-scoped client — picks internal vs external transport. Each fn keeps
 * the exact signature the mutations already expect so the optimistic bodies are
 * unchanged.
 */
function acceptanceClient(audience: AcceptanceAudience) {
  if (audience === 'external') {
    return {
      fetch: fetchAcceptanceExternal,
      add: addAcceptanceItemExternal,
      toggle: (taskId: number, itemId: string, done: boolean) =>
        patchAcceptanceItemExternal(taskId, itemId, { done }),
      updateText: (taskId: number, itemId: string, text: string) =>
        patchAcceptanceItemExternal(taskId, itemId, { text }),
      remove: deleteAcceptanceItemExternal,
    };
  }
  return {
    fetch: fetchAcceptance,
    add: addAcceptanceItem,
    toggle: toggleAcceptanceItem,
    updateText: updateAcceptanceItemText,
    remove: deleteAcceptanceItem,
  };
}

function statusOf(err: unknown): number | undefined {
  if (err instanceof ApiClientError) return err.statusCode;
  if (err instanceof ExternalApiError) return err.statusCode;
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const s = (err as { statusCode?: unknown }).statusCode;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

interface AcceptanceContext {
  previous: AcceptanceItem[] | undefined;
}

function toastMessage(toast: ReturnType<typeof useToast>) {
  return (err: unknown, fallback: string) => {
    const status = statusOf(err);
    if (status === 403) {
      toast('Not allowed to change acceptance criteria.', 'error');
      return;
    }
    if (status === 404) {
      toast('Item no longer exists — refreshing list.', 'warning');
      return;
    }
    toast(fallback, 'error');
  };
}

export function useTaskAcceptance(
  taskId: number | null,
  audience: AcceptanceAudience = 'internal',
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const announce = toastMessage(toast);
  const client = acceptanceClient(audience);
  const keyFor = (id: number) => acceptanceQueryKey(id, audience);

  const query = useQuery<AcceptanceItem[]>({
    queryKey: taskId != null ? keyFor(taskId) : ['tasks', 'noop', 'acceptance', audience],
    queryFn: () => client.fetch(taskId as number),
    enabled: taskId != null,
    staleTime: 30_000,
  });

  function snapshot(): AcceptanceItem[] | undefined {
    if (taskId == null) return undefined;
    return queryClient.getQueryData<AcceptanceItem[]>(keyFor(taskId));
  }

  function setLocal(next: AcceptanceItem[] | undefined) {
    if (taskId == null) return;
    queryClient.setQueryData<AcceptanceItem[]>(keyFor(taskId), next ?? []);
  }

  function invalidate() {
    if (taskId == null) return;
    queryClient.invalidateQueries({ queryKey: keyFor(taskId) });
    queryClient.invalidateQueries({ queryKey: MY_TASKS_BASE_KEY });
  }

  // Pending items get a temp id so we can match them in onError rollback.
  const addItem = useMutation<AcceptanceItem, Error, string, AcceptanceContext>({
    mutationFn: (text) => client.add(taskId as number, text),
    onMutate: async (text) => {
      if (taskId == null) return { previous: undefined };
      await queryClient.cancelQueries({ queryKey: keyFor(taskId) });
      const previous = snapshot();
      const optimistic: AcceptanceItem = {
        id: `pending-${Date.now()}`,
        text,
        done: false,
      };
      setLocal([...(previous ?? []), optimistic]);
      return { previous };
    },
    onError: (err, _vars, context) => {
      setLocal(context?.previous);
      announce(err, "Couldn't add acceptance item.");
    },
    onSettled: () => invalidate(),
  });

  interface ToggleArgs {
    itemId: string;
    done: boolean;
  }
  const toggleItem = useMutation<AcceptanceItem, Error, ToggleArgs, AcceptanceContext>({
    mutationFn: ({ itemId, done }) =>
      client.toggle(taskId as number, itemId, done),
    onMutate: async ({ itemId, done }) => {
      if (taskId == null) return { previous: undefined };
      await queryClient.cancelQueries({ queryKey: keyFor(taskId) });
      const previous = snapshot();
      if (previous) {
        setLocal(previous.map((it) => (it.id === itemId ? { ...it, done } : it)));
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      setLocal(context?.previous);
      announce(err, "Couldn't update acceptance item.");
    },
    onSettled: () => invalidate(),
  });

  interface UpdateTextArgs {
    itemId: string;
    text: string;
  }
  const updateText = useMutation<AcceptanceItem, Error, UpdateTextArgs, AcceptanceContext>({
    mutationFn: ({ itemId, text }) =>
      client.updateText(taskId as number, itemId, text),
    onMutate: async ({ itemId, text }) => {
      if (taskId == null) return { previous: undefined };
      await queryClient.cancelQueries({ queryKey: keyFor(taskId) });
      const previous = snapshot();
      if (previous) {
        setLocal(previous.map((it) => (it.id === itemId ? { ...it, text } : it)));
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      setLocal(context?.previous);
      announce(err, "Couldn't rename acceptance item.");
    },
    onSettled: () => invalidate(),
  });

  const deleteItem = useMutation<void, Error, string, AcceptanceContext>({
    mutationFn: (itemId) => client.remove(taskId as number, itemId),
    onMutate: async (itemId) => {
      if (taskId == null) return { previous: undefined };
      await queryClient.cancelQueries({ queryKey: keyFor(taskId) });
      const previous = snapshot();
      if (previous) {
        setLocal(previous.filter((it) => it.id !== itemId));
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      setLocal(context?.previous);
      announce(err, "Couldn't remove acceptance item.");
    },
    onSettled: () => invalidate(),
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    addItem,
    toggleItem,
    updateText,
    deleteItem,
  };
}

export type { AcceptanceItem };
